import React, { useEffect, useRef, useState } from "react";
import { loadGoogleMaps, isMapsConfigured, pinIcon, GMAP_LIGHT_STYLE } from "../lib/googleMaps";

// Reusable interactive Google map for single-location, area and bbox views.
// Props:
//   center   {lat,lng}        — initial centre (optional)
//   zoom     number           — initial zoom (default 13)
//   geocode  string           — address/place to geocode + centre on (optional)
//   bounds   {north,south,east,west} — fit to this box (optional)
//   markers  [{lat,lng,title,info}]  — pins to drop (optional)
//   height   number|string    — map height (default 340)
//   light    bool             — use the soft calm basemap style
export const SimpleGoogleMap = ({
  center, zoom = 13, geocode, bounds, markers = [],
  height = 340, light = false, testId = "google-map", fullscreenQuery, style = {},
}) => {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [failed, setFailed] = useState(false);
  const depKey = JSON.stringify({ center, zoom, geocode, bounds, markers });

  useEffect(() => {
    if (!isMapsConfigured()) { setFailed(true); return; }
    let cancelled = false;
    loadGoogleMaps(["geometry"]).then((google) => {
      if (cancelled || !elRef.current) return;
      if (!mapRef.current) {
        mapRef.current = new google.maps.Map(elRef.current, {
          center: center || { lat: 49.25, lng: -123.0 },
          zoom,
          mapTypeControl: false,
          streetViewControl: true,
          fullscreenControl: true,
          gestureHandling: "cooperative",
          ...(light ? { styles: GMAP_LIGHT_STYLE } : {}),
        });
      }
      const map = mapRef.current;
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];

      (markers || []).forEach((mk) => {
        if (typeof mk.lat !== "number" || typeof mk.lng !== "number") return;
        const marker = new google.maps.Marker({
          position: { lat: mk.lat, lng: mk.lng }, map,
          title: mk.title || "", icon: pinIcon(google),
        });
        if (mk.info) {
          const iw = new google.maps.InfoWindow({ content: mk.info });
          marker.addListener("click", () => iw.open({ anchor: marker, map }));
        }
        markersRef.current.push(marker);
      });

      if (bounds) {
        map.fitBounds(new google.maps.LatLngBounds(
          { lat: bounds.south, lng: bounds.west },
          { lat: bounds.north, lng: bounds.east },
        ));
      } else if (markers && markers.length > 1) {
        const b = new google.maps.LatLngBounds();
        markers.forEach((mk) => b.extend({ lat: mk.lat, lng: mk.lng }));
        map.fitBounds(b);
      } else if (markers && markers.length === 1) {
        map.setCenter({ lat: markers[0].lat, lng: markers[0].lng });
        map.setZoom(zoom);
      } else if (geocode) {
        new google.maps.Geocoder().geocode({ address: geocode }, (res, status) => {
          if (cancelled || status !== "OK" || !res || !res[0]) return;
          const g = res[0].geometry;
          if (g.viewport) map.fitBounds(g.viewport);
          else { map.setCenter(g.location); map.setZoom(zoom); }
          const marker = new google.maps.Marker({ position: g.location, map, icon: pinIcon(google) });
          markersRef.current.push(marker);
        });
      } else if (center) {
        map.setCenter(center);
        map.setZoom(zoom);
      }
    }).catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey]);

  if (failed) {
    const q = encodeURIComponent(geocode || fullscreenQuery || "British Columbia, Canada");
    return (
      <div data-testid={`${testId}-fallback`} style={{ width: "100%", height, display: "grid", placeItems: "center", background: "#EDF1F7", borderRadius: 12, textAlign: "center", padding: 16, boxSizing: "border-box", ...style }}>
        <div style={{ fontFamily: "Inter,sans-serif", fontSize: "0.85rem", color: "#4b5563" }}>
          Map unavailable.{" "}
          <a href={`https://www.google.com/maps/search/?api=1&query=${q}`} target="_blank" rel="noopener noreferrer" style={{ color: "#0A3D99", fontWeight: 700 }}>Open in Google Maps ↗</a>
        </div>
      </div>
    );
  }

  return <div ref={elRef} data-testid={testId} style={{ width: "100%", height, borderRadius: 12, overflow: "hidden", ...style }} />;
};

export default SimpleGoogleMap;
